/**
 * Properties of the selected form field, while preparing a form.
 */

import { useEffect, useState } from 'react'
import type { FieldSuggestion, FormField } from '../api'

interface Props {
  field: FormField | null
  suggestions: number
  onChange: (changes: Record<string, unknown>) => void
  onDelete: () => void
  onDetect: () => void
  onAcceptAll: () => void
  onDismissAll: () => void
  onClose: () => void
}

const KIND_LABEL: Record<string, string> = {
  text: 'Text field', checkbox: 'Checkbox', radio: 'Radio buttons', combo: 'Dropdown',
  list: 'List box', signature: 'Signature field', button: 'Button',
}

export function FieldPanel({ field, suggestions, onChange, onDelete, onDetect, onAcceptAll, onDismissAll, onClose }: Props) {
  const [name, setName] = useState('')
  const [tooltip, setTooltip] = useState('')
  const [options, setOptions] = useState('')
  const [maxLength, setMaxLength] = useState('')

  useEffect(() => {
    setName(field?.name ?? '')
    setTooltip(field?.tooltip ?? '')
    setOptions(field?.options.map((o) => o.display).join('\n') ?? '')
    setMaxLength(field?.maxLength ? String(field.maxLength) : '')
  }, [field])

  return (
    <aside className="comments fieldpanel" aria-label="Field properties">
      <div className="comments__header">
        <span>Prepare form</span>
        <button className="iconbtn" title="Done" onClick={onClose}>{'✕'}</button>
      </div>
      <div className="fieldpanel__body">
        <div className="fieldpanel__detect">
          <button className="btn btn--small" onClick={onDetect}>Detect fields on this page</button>
          {suggestions > 0 && (
            <>
              <span>{suggestions} suggested</span>
              <button className="btn btn--small btn--primary" onClick={onAcceptAll}>Add all</button>
              <button className="btn btn--small" onClick={onDismissAll}>Dismiss</button>
            </>
          )}
        </div>
        <p className="modal__hint">
          Pick a field type in the ribbon and drag a box on the page. Click a field to
          change it; drag to move it, or its corner to resize.
        </p>

        {field && (
          <form className="fieldpanel__form" onSubmit={(e) => e.preventDefault()}>
            <h3>{KIND_LABEL[field.kind] ?? field.kind}</h3>
            <label className="field">
              <span>Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     onBlur={() => name.trim() && name !== field.name && onChange({ name: name.trim() })} />
            </label>
            <label className="field">
              <span>Tooltip &mdash; what a screen reader announces</span>
              <input value={tooltip} placeholder="e.g. Your full name"
                     onChange={(e) => setTooltip(e.target.value)}
                     onBlur={() => tooltip !== field.tooltip && onChange({ tooltip })} />
            </label>
            <label className="check">
              <input type="checkbox" checked={field.required} onChange={(e) => onChange({ required: e.target.checked })} />
              Required
            </label>
            <label className="check">
              <input type="checkbox" checked={field.readOnly} onChange={(e) => onChange({ readOnly: e.target.checked })} />
              Read-only
            </label>

            {field.kind === 'text' && (
              <>
                <label className="check">
                  <input type="checkbox" checked={field.multiline} onChange={(e) => onChange({ multiline: e.target.checked })} />
                  Multiple lines
                </label>
                <div className="field-row">
                  <label className="field">
                    <span>Maximum characters</span>
                    <input type="number" min={0} value={maxLength} onChange={(e) => setMaxLength(e.target.value)}
                           onBlur={() => onChange({ maxLength: maxLength ? Number(maxLength) : null })} />
                  </label>
                  <label className="field">
                    <span>Font size (0 = auto)</span>
                    <input type="number" min={0} step={0.5} defaultValue={field.fontSize} key={field.fontSize}
                           onBlur={(e) => onChange({ fontSize: Number(e.target.value) || 0 })} />
                  </label>
                </div>
                <label className="check">
                  <input type="checkbox" checked={field.comb} disabled={!field.maxLength}
                         onChange={(e) => onChange({ comb: e.target.checked })} />
                  One box per character (comb){field.maxLength ? '' : ' — set a maximum first'}
                </label>
                <label className="field">
                  <span>Alignment</span>
                  <select value={field.align} onChange={(e) => onChange({ align: Number(e.target.value) })}>
                    <option value={0}>Left</option>
                    <option value={1}>Centre</option>
                    <option value={2}>Right</option>
                  </select>
                </label>
              </>
            )}

            {(field.kind === 'combo' || field.kind === 'list') && (
              <label className="field">
                <span>Choices, one per line</span>
                <textarea rows={6} value={options} onChange={(e) => setOptions(e.target.value)}
                          onBlur={() => onChange({ options: options.split('\n') })} />
              </label>
            )}

            {(field.kind === 'checkbox' || field.kind === 'radio') && (
              <p className="modal__hint">
                {field.kind === 'radio'
                  ? `Buttons: ${field.options.map((o) => o.export).join(', ')}. With this group selected, draw with the Radio tool to add another button to it.`
                  : `Value when ticked: ${field.options[0]?.export ?? 'Yes'}`}
              </p>
            )}

            <div className="fieldpanel__look">
              <label className="check">
                <input type="checkbox" defaultChecked onChange={(e) => onChange({ border: e.target.checked })} />
                Border
              </label>
              <label className="check">
                <input type="checkbox" onChange={(e) => onChange({ fill: e.target.checked })} />
                Tinted background
              </label>
            </div>

            <button type="button" className="btn btn--danger" onClick={onDelete}>Delete field</button>
          </form>
        )}
      </div>
    </aside>
  )
}

export type { FieldSuggestion }
